import React, {useMemo} from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {SelectField} from '@netx/core-components/SelectField';
import {fileFormatFamilies} from '@netx/core-search/properties/search';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {CommonRuleEditorProps} from '../../../types';
import {
	fileFormatFamilyRuleEditorClasses,
	getFileFormatFamilyRuleEditorUtilityClass,
	FileFormatFamilyRuleEditorClasses,
	FILE_FORMAT_FAMILYRULE_EDITOR_CLASS_NAME,
} from './fileFormatFamilyRuleEditorClasses';

export type FileFormatFamilyRuleEditorOwnerState = WithClasses<
	FileFormatFamilyRuleEditorClasses,
	{}
>;

const useUtilityClasses = (
	ownerState: FileFormatFamilyRuleEditorOwnerState,
) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(
		slots,
		getFileFormatFamilyRuleEditorUtilityClass,
		classes,
	);
};

const FileFormatFamilyRuleEditorRoot = styled('div', {
	name: FILE_FORMAT_FAMILYRULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
}));

export type FileFormatFamilyRuleEditorProps = WithClasses<
	FileFormatFamilyRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementValue1: string;
		onValue1Change: (value1: string) => void;
	}
>;

const FileFormatFamilyRuleEditorInner = ({
	classes: _classes,
	className,
	elementValue1,
	validationErrors,
	onValue1Change,
}: FileFormatFamilyRuleEditorProps) => {
	const ownerState: FileFormatFamilyRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const valueOptions = useMemo(() => Object.values(fileFormatFamilies), []);

	return (
		<FileFormatFamilyRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<string, false, true, false>
				data-testid="file-format-family-rule-value-1"
				disableClearable
				error={validationErrors?.elementValue1?.message}
				label={i18n.t('i18n.fileFormatFamily')}
				name="rule-value-1"
				options={valueOptions}
				value={elementValue1}
				onChange={(event, value) => onValue1Change(value)}
			/>
		</FileFormatFamilyRuleEditorRoot>
	);
};

export const FileFormatFamilyRuleEditorPrimitive =
	FileFormatFamilyRuleEditorInner;

export const FileFormatFamilyRuleEditor = FileFormatFamilyRuleEditorPrimitive;

export default FileFormatFamilyRuleEditor;

export type FileFormatFamilyRuleEditorGenericProps = WithClasses<
	FileFormatFamilyRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const FileFormatFamilyRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: FileFormatFamilyRuleEditorGenericProps) => {
	return (
		<FileFormatFamilyRuleEditor
			{...props}
			elementValue1={elementValue1.toString()}
		/>
	);
};

export const FileFormatFamilyRuleEditorGeneric =
	FileFormatFamilyRuleEditorGenericPrimitive;
